import React from 'react'
import { connect } from 'react-redux'
import { TidewaysContext } from '../app/context'
import { ApplicationState } from '../redux/ApplicationState'
import NavigationLink from './components/NavigationLink'
import { NavigationItem, bottomNavigation, NavigationItemFilterCheck } from './sidebarHelper'
import { ProjectNavigationItems } from './components/ProjectNavigationItems'
import { DashboardEntity } from '../redux/dashboard/DashboardState'
import { Entity } from '../app/entity'
import CollapseNavigationButton from './components/CollapseNavigationButton'
import { Tooltip } from 'react-tooltip'
import { OrganizationDropdown } from './components/OrganizationDropdown'
import { getNavigationState } from '../redux/V2/V2State'
import { NavigationState } from '../redux/V2/Navigation/NavigationState'
import { fetchNavigation } from '../redux/V2/Navigation/actions'
import Raven from 'raven-js'

interface StateProps {
    tidewaysContext: TidewaysContext
    dashboard: Entity<DashboardEntity> | null
    navigationData: NavigationState
}

interface DispatchProps {
    fetchNavigation: typeof fetchNavigation
}

type Props = StateProps & DispatchProps

interface State {
    isAsideNavCollapsed: boolean
}

export type NavigationContext = 'project' | 'organization-settings' | 'dashboard' | 'user-settings' | 'project-settings'

const sideBarCollapsedCookieName = 'TIDEWAYS_COLLAPSED_NAV'

export class SideBarNavigation extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = {
            isAsideNavCollapsed: this.isAsideNavCollapsedFromCookie(),
        }
    }

    public componentDidMount() {
        this.setState({ isAsideNavCollapsed: this.isAsideNavCollapsedFromCookie() })

        if (!this.props.navigationData.loaded) {
            this.props.fetchNavigation()
        }
    }

    handleAsideNavCollapsing = () => {
        this.setState(
            {
                isAsideNavCollapsed: !this.state.isAsideNavCollapsed,
            },
            () => {
                if (this.state.isAsideNavCollapsed) {
                    document.cookie = sideBarCollapsedCookieName + '=1; path=/; max-age=31536000; SameSite=strict; Secure;'
                } else {
                    document.cookie = sideBarCollapsedCookieName + '=; path=/; Max-Age=-99999999;'
                }
            },
        )
    }

    isAsideNavCollapsedFromCookie = () => {
        return document.cookie.indexOf(sideBarCollapsedCookieName + '=1') !== -1
    }

    private detectNavigationContext = (props: Props): NavigationContext => {
        const { tidewaysContext, dashboard } = props

        if (tidewaysContext.navigation.navigationContext === 'project-settings') {
            return 'project-settings'
        }

        if (dashboard || tidewaysContext.route.route === 'dashboard') {
            return 'dashboard'
        }

        if (tidewaysContext.navigation.navigationContext === 'organization-settings') {
            return 'organization-settings'
        }

        if (tidewaysContext.navigation.navigationContext === 'user-settings') {
            return 'user-settings'
        }

        if (tidewaysContext.application) {
            return 'project'
        }

        Raven.captureException(new Error('Unknown navigation context'))

        return 'dashboard'
    }

    public render() {
        const { tidewaysContext, navigationData } = this.props
        const { route } = tidewaysContext
        const hasCollapsedStyle = this.state.isAsideNavCollapsed ? 'sideNav--collapsed' : ''
        const cnSideNav = `sideNav ${hasCollapsedStyle}`

        const navigationContext = this.detectNavigationContext(this.props)

        const currentOrganization: string | undefined =
            tidewaysContext.organization?.name ||
            tidewaysContext.route.parameters?.current ||
            tidewaysContext.defaultOrganizationName ||
            navigationData.organizations[0]?.name

        let applications: string[] = []
        if (navigationData.loaded) {
            const currentOrg = navigationData.organizations.find(organization => organization.name === currentOrganization) // applications = context.navigation.organizationSettings.applications
            if (currentOrg) {
                applications = currentOrg.applications
            }
        }

        const filterProps: NavigationItemFilterCheck = {
            license: tidewaysContext.application?.license,
            isProfilingSpace: tidewaysContext.isProfilingSpace(),
            tidewaysContext,
        }

        return (
            <>
                <nav className={cnSideNav}>
                    <OrganizationDropdown
                        current={currentOrganization || undefined}
                        showTooltip={this.state.isAsideNavCollapsed}
                        application={tidewaysContext.application?.name}
                        applications={applications}
                        navigationContext={navigationContext}
                    />
                    {tidewaysContext.navigation.statusPage.isError && (
                        <div
                            className="alert alert-danger"
                            role="alert"
                            data-tooltip-id="navTooltip"
                            data-tooltip-content={tidewaysContext.navigation.statusPage.label}
                            data-tooltip-hidden={!this.state.isAsideNavCollapsed}
                        >
                            <a href="https://status.tideways.io">
                                <strong>Warning</strong>
                                {!this.state.isAsideNavCollapsed && (
                                    <>
                                        <br />
                                        {tidewaysContext.navigation.statusPage.label}
                                    </>
                                )}
                            </a>
                        </div>
                    )}
                    {(navigationContext === 'project' || navigationContext === 'project-settings') && (
                        <ProjectNavigationItems
                            filterProps={filterProps}
                            route={route}
                            showTooltip={this.state.isAsideNavCollapsed}
                            navigationContext={navigationContext}
                            currentOrganization={currentOrganization}
                        />
                    )}

                    {tidewaysContext.navigation.productUpdate ? (
                        <div className="sideNav-info bottom">
                            <div className="sideNav-title">
                                <a
                                    href={tidewaysContext.navigation.productUpdate.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    title={tidewaysContext.navigation.productUpdate.title}
                                >
                                    <span className="badge">new</span>
                                    {this.state.isAsideNavCollapsed ? null : tidewaysContext.navigation.productUpdate.title}
                                </a>
                            </div>
                        </div>
                    ) : null}
                    <ul className={'sideNav-list ' + (tidewaysContext.navigation.productUpdate ? '' : 'bottom')}>
                        {bottomNavigation
                            .filter(item => currentOrganization || !item.parametersCallback)
                            .map((item: NavigationItem, index: number) => {
                                return (
                                    <li className="sideNav-listItem" key={index}>
                                        <NavigationLink
                                            icon={item.icon}
                                            label={item.label}
                                            isActive={
                                                route.route === item.route ||
                                                (item.alternativeRoutes && route.route in item.alternativeRoutes) ||
                                                false
                                            }
                                            route={item.route}
                                            parameters={
                                                item.parametersCallback ? item.parametersCallback(currentOrganization) : item.parameters
                                            }
                                            modal={item.modal}
                                            setSkipHistoryApi
                                            showTooltip={this.state.isAsideNavCollapsed}
                                        />
                                    </li>
                                )
                            })}
                        {tidewaysContext.navigation.extra.map(item => (
                            <li className="sideNav-listItem" key={item.label}>
                                <NavigationLink
                                    url={item.url}
                                    label={item.label}
                                    icon={item.icon}
                                    isActive={false}
                                    setSkipHistoryApi
                                    showTooltip={this.state.isAsideNavCollapsed}
                                />
                            </li>
                        ))}
                        <li className="sideNav-listItem" data-tooltip-id="navTooltip" data-tooltip-content="hide/show navigation">
                            <CollapseNavigationButton
                                onToggleNavigation={this.handleAsideNavCollapsing}
                                isCollapsed={this.state.isAsideNavCollapsed}
                            />
                        </li>
                    </ul>
                </nav>
                <Tooltip id="navTooltip" className="tw-react-tooltip" />
            </>
        )
    }
}

export default connect<StateProps>(
    (globalState: ApplicationState): StateProps => {
        const { dashboard } = globalState.dashboard
        const navigationData = getNavigationState(globalState)

        return {
            tidewaysContext: globalState.tidewaysContext,
            dashboard,
            navigationData,
        }
    },
    { fetchNavigation },
)(SideBarNavigation)
